import { util as sdkUtil } from '@vansite/ts-sharetribe-flex-sdk';
import * as sdkTypes from '@vansite/ts-sharetribe-flex-sdk/types';
import moment from 'moment';
import { validate } from 'uuid';

import { FacebookViewContentEvent } from '../../analytics/facebook';
import { createListingItem } from '../../analytics/conversions';
import { FirebaseViewItemEvent } from '../../analytics/firebase';
import config from '../../config';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchTranslations } from '../../ducks/translations.duck';
import { fetchCurrentUser, fetchCurrentUserHasOrders } from '../../ducks/user.duck';
import { annualPassValidateSuccess, voucherValidateFailed, voucherValidateRequest, voucherValidateSuccess } from '../../ducks/voucherify.duck';
import {
  buildOwnedAnnualPassVoucher,
  hasCurrentUserValidAnnualPass,
  hasCurrentUserValidAnnualPassType,
  PASS_ANNUAL_PASS_VOUCHER,
} from '../../util/annualPass';
import { transactionLineItems } from '../../util/api';
import { whenAnalyticsReady } from '../../util/consent/initConsent';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { getLocaleFromPath } from '../../util/locale';
import { logError } from '../../util/log';
import { boundsAroundPoint, getDistanceBetween } from '../../util/maps';
import { LISTING_STATE_PUBLISHED } from '../../util/types';
import { LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT } from '../../util/urlHelpers';
import reducerRegistry from '../../ducks/reducerRegistry';

const { UUID, LatLng } = sdkTypes;

const ANNUAL_PASS_SAME_PROVIDER_BOOKING_LIMIT = config.annualPass.sameProviderBookingLimit;

// ================ Nearby listings config ================ //

export const NEARBY_RADIUS_KM = 50; // half-side of the bounds box around the listing
export const NEARBY_MAX = 10; // max nearby places rendered
export const NEARBY_MIN = 1; // hide the section below this many results
const NEARBY_CANDIDATE_COUNT = 50; // candidates fetched before ranking

// Sparse fieldset for the nearby query — only what a ListingCard needs.
// Mirrors RoutePlannerPage.duck.js LISTINGS_QUERY_FIELDS.
const NEARBY_LISTING_FIELDS = {
  include: ['images'],
  'fields.listing': [
    'title',
    'geolocation',
    'price',
    'createdAt',
    'publicData.category',
    'publicData.locationPlace',
    'publicData.verified',
    'metadata.rating',
    'metadata.ratingCount',
    'metadata.explorerListing',
    'metadata.annualPassPrice',
  ],
  'fields.image': ['variants.landscape-crop', 'variants.landscape-crop2x'],
  'limit.images': 1,
  pub_hidden: false,
};

/**
 * Rank candidate listings for the "nearby" section: best rating first,
 * ties broken by rating count, unrated listings last. The current listing
 * is excluded. Returns at most NEARBY_MAX listings. Pure — does not mutate
 * the input array.
 *
 * @param {Array} listings - raw API listing objects (with id.uuid + attributes.metadata)
 * @param {string} currentListingUuid - uuid of the listing being viewed
 * @returns {Array}
 */
export const rankNearbyListings = (listings, currentListingUuid) =>
  (listings || [])
    .filter((l) => l?.id?.uuid && l.id.uuid !== currentListingUuid)
    .sort((a, b) => {
      const ra = a.attributes?.metadata?.rating || 0;
      const rb = b.attributes?.metadata?.rating || 0;
      if (rb !== ra) return rb - ra;
      const ca = a.attributes?.metadata?.ratingCount || 0;
      const cb = b.attributes?.metadata?.ratingCount || 0;
      return cb - ca;
    })
    .slice(0, NEARBY_MAX);

const track = (listing, currentUser) => {
  try {
    if (listing?.attributes?.state !== LISTING_STATE_PUBLISHED) return;
    if (!listing.attributes.price?.amount || !listing.attributes.price?.currency) return;
    const value = listing.attributes.price.amount / 100;
    const currency = listing.attributes.price.currency.toLowerCase();

    FirebaseViewItemEvent.withValue(value)
      .withCurrency(currency)
      .withItems(createListingItem(listing))
      .logEvent();
    FacebookViewContentEvent.withCurrentUserUUID(currentUser?.id?.uuid)
      .withValue(value)
      .withCurrency(currency)
      .withContentName(listing.attributes.title)
      .withContentCategory(listing.attributes.publicData?.category)
      .withContentType('listing')
      .withContentIds([listing.id.uuid])
      .withContents([{ id: listing.id.uuid, quantity: 1, item_price: value }])
      .logEvent();
  } catch (e) {
    logError(e, 'track-view-item-event-failed');
  }
};

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/ListingPage/SET_INITIAL_VALUES';

export const SHOW_LISTING_REQUEST = 'app/ListingPage/SHOW_LISTING_REQUEST';
export const SHOW_LISTING_ERROR = 'app/ListingPage/SHOW_LISTING_ERROR';

export const FETCH_REVIEWS_REQUEST = 'app/ListingPage/FETCH_REVIEWS_REQUEST';
export const FETCH_REVIEWS_SUCCESS = 'app/ListingPage/FETCH_REVIEWS_SUCCESS';
export const FETCH_REVIEWS_ERROR = 'app/ListingPage/FETCH_REVIEWS_ERROR';

export const FETCH_TIME_SLOTS_REQUEST = 'app/ListingPage/FETCH_TIME_SLOTS_REQUEST';
export const FETCH_TIME_SLOTS_SUCCESS = 'app/ListingPage/FETCH_TIME_SLOTS_SUCCESS';
export const FETCH_TIME_SLOTS_ERROR = 'app/ListingPage/FETCH_TIME_SLOTS_ERROR';

export const FETCH_LINE_ITEMS_REQUEST = 'app/ListingPage/FETCH_LINE_ITEMS_REQUEST';
export const FETCH_LINE_ITEMS_SUCCESS = 'app/ListingPage/FETCH_LINE_ITEMS_SUCCESS';
export const FETCH_LINE_ITEMS_ERROR = 'app/ListingPage/FETCH_LINE_ITEMS_ERROR';
export const FETCH_TRANSACTIONS_WITH_PROVIDER_REQUEST =
  'app/ListingPage/FETCH_TRANSACTIONS_WITH_PROVIDER_REQUEST';
export const FETCH_TRANSACTIONS_WITH_PROVIDER_SUCCESS =
  'app/ListingPage/FETCH_TRANSACTIONS_WITH_PROVIDER_SUCCESS';
export const FETCH_TRANSACTIONS_WITH_PROVIDER_ERROR =
  'app/ListingPage/FETCH_TRANSACTIONS_WITH_PROVIDER_ERROR';

export const FETCH_NEARBY_REQUEST = 'app/ListingPage/FETCH_NEARBY_REQUEST';
export const FETCH_NEARBY_SUCCESS = 'app/ListingPage/FETCH_NEARBY_SUCCESS';
export const FETCH_NEARBY_ERROR = 'app/ListingPage/FETCH_NEARBY_ERROR';

// ================ Reducer ================ //

/** @type {import('../../../types/vansite').VanSite.State.ListingPage} */
const initialState = {
  id: null,
  showListingError: null,
  reviews: [],
  fetchReviewsError: null,
  timeSlots: [],
  lineItems: [],
  fetchTimeSlotsInProgress: false,
  fetchTimeSlotsError: null,
  fetchLineItemsInProgress: false,
  fetchLineItemsError: null,
  enquiryModalOpenForListingId: null,
  toManyTransactionsWithProvider: false,
  toManyTransactionsWithProviderInProgress: false,
  toManyTransactionsWithProviderError: null,
  nearbyListingIds: [],
  fetchNearbyInProgress: false,
  fetchNearbyError: null,
};

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case SHOW_LISTING_REQUEST:
      return { ...state, id: payload.id, showListingError: null };
    case SHOW_LISTING_ERROR:
      return { ...state, showListingError: payload };

    case FETCH_REVIEWS_REQUEST:
      return { ...state, fetchReviewsError: null };
    case FETCH_REVIEWS_SUCCESS:
      return { ...state, reviews: payload };
    case FETCH_REVIEWS_ERROR:
      return { ...state, fetchReviewsError: payload };

    case FETCH_TIME_SLOTS_REQUEST:
      return { ...state, fetchTimeSlotsInProgress: true, fetchTimeSlotsError: null };
    case FETCH_TIME_SLOTS_SUCCESS:
      return { ...state, fetchTimeSlotsInProgress: false, timeSlots: payload };
    case FETCH_TIME_SLOTS_ERROR:
      return { ...state, fetchTimeSlotsInProgress: false, fetchTimeSlotsError: payload };

    case FETCH_LINE_ITEMS_REQUEST:
      return { ...state, fetchLineItemsInProgress: true, fetchLineItemsError: null };
    case FETCH_LINE_ITEMS_SUCCESS:
      return { ...state, fetchLineItemsInProgress: false, lineItems: payload };
    case FETCH_LINE_ITEMS_ERROR:
      return { ...state, fetchLineItemsInProgress: false, fetchLineItemsError: payload };

    case FETCH_TRANSACTIONS_WITH_PROVIDER_REQUEST:
      return {
        ...state,
        toManyTransactionsWithProviderInProgress: true,
        toManyTransactionsWithProviderError: null,
      };
    case FETCH_TRANSACTIONS_WITH_PROVIDER_SUCCESS:
      return {
        ...state,
        toManyTransactionsWithProviderInProgress: false,
        toManyTransactionsWithProvider: payload,
      };
    case FETCH_TRANSACTIONS_WITH_PROVIDER_ERROR:
      return {
        ...state,
        toManyTransactionsWithProviderInProgress: false,
        toManyTransactionsWithProviderError: payload,
      };

    case FETCH_NEARBY_REQUEST:
      return { ...state, fetchNearbyInProgress: true, fetchNearbyError: null };
    case FETCH_NEARBY_SUCCESS:
      return { ...state, fetchNearbyInProgress: false, nearbyListingIds: payload };
    case FETCH_NEARBY_ERROR:
      return { ...state, fetchNearbyInProgress: false, fetchNearbyError: payload };

    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Action creators ================ //

export const setInitialValues = (initialValues) => ({
  type: SET_INITIAL_VALUES,
  payload: Object.fromEntries(
    Object.keys(initialState)
      .filter((key) => key in initialValues)
      .map((key) => [key, initialValues[key]])
  ),
});

export const showListingRequest = (id) => ({
  type: SHOW_LISTING_REQUEST,
  payload: { id },
});

export const showListingError = (e) => ({
  type: SHOW_LISTING_ERROR,
  error: true,
  payload: e,
});

export const fetchReviewsRequest = () => ({ type: FETCH_REVIEWS_REQUEST });
export const fetchReviewsSuccess = (reviews) => ({ type: FETCH_REVIEWS_SUCCESS, payload: reviews });
export const fetchReviewsError = (error) => ({
  type: FETCH_REVIEWS_ERROR,
  error: true,
  payload: error,
});

export const fetchTimeSlotsRequest = () => ({ type: FETCH_TIME_SLOTS_REQUEST });
export const fetchTimeSlotsSuccess = (timeSlots) => ({
  type: FETCH_TIME_SLOTS_SUCCESS,
  payload: timeSlots,
});
export const fetchTimeSlotsError = (error) => ({
  type: FETCH_TIME_SLOTS_ERROR,
  error: true,
  payload: error,
});

export const fetchLineItemsRequest = () => ({ type: FETCH_LINE_ITEMS_REQUEST });
export const fetchLineItemsSuccess = (lineItems) => ({
  type: FETCH_LINE_ITEMS_SUCCESS,
  payload: lineItems,
});
export const fetchLineItemsError = (error) => ({
  type: FETCH_LINE_ITEMS_ERROR,
  error: true,
  payload: error,
});

export const fetchTransactionsWithProviderRequest = () => ({
  type: FETCH_TRANSACTIONS_WITH_PROVIDER_REQUEST,
});
export const fetchTransactionsWithProviderSuccess = (toManyTransactionsWithProvider) => ({
  type: FETCH_TRANSACTIONS_WITH_PROVIDER_SUCCESS,
  payload: toManyTransactionsWithProvider,
});
export const fetchTransactionsWithProviderError = (error) => ({
  type: FETCH_TRANSACTIONS_WITH_PROVIDER_ERROR,
  error: true,
  payload: error,
});

export const fetchNearbyRequest = () => ({ type: FETCH_NEARBY_REQUEST });
export const fetchNearbySuccess = (listingIds) => ({
  type: FETCH_NEARBY_SUCCESS,
  payload: listingIds,
});
export const fetchNearbyError = (error) => ({
  type: FETCH_NEARBY_ERROR,
  error: true,
  payload: error,
});

// ================ Thunks ================ //

/**
 * Logs view_item (GA4) and ViewContent (Meta) for a published listing. Waits
 * for the analytics vendors, so it also works for the server-rendered landing
 * page, and reads the current user only when it fires.
 * @param {object} listing listing entity
 */
export const trackListingView = (listing) => async (dispatch, getState) => {
  await whenAnalyticsReady();
  track(listing, getState().user.currentUser);
};

export const showListing =
  (listingId, locale = config.locale, isOwn = false) =>
    async (dispatch, getState, sdk) => {
      dispatch(showListingRequest(listingId));
      dispatch(fetchCurrentUser());

      const params = {
        id: listingId,
        include: ['author', 'author.profileImage', 'images'],
        'fields.image': [
          // Listing page
          'variants.landscape-crop',
          'variants.landscape-crop-x05',
          'variants.landscape-crop1x',
          'variants.landscape-crop2x',
          'variants.landscape-crop3x',
          'variants.landscape-crop4x',
          'variants.surround-crop',

          // Social media
          'variants.facebook',
          'variants.twitter',

          // Image carousel
          'variants.scaled-small',
          'variants.scaled-medium',
          'variants.scaled-large',
          'variants.scaled-xlarge',

          // Avatars
          'variants.square-small',
          'variants.square-small2x',
        ],
        'imageVariant.landscape-crop-x05': sdkUtil.objectQueryString({
          w: 400,
          h: 267,
          fit: 'crop',
        }),
        'imageVariant.landscape-crop1x': sdkUtil.objectQueryString({
          w: 600,
          h: 400,
          fit: 'crop',
        }),
        'imageVariant.landscape-crop2x': sdkUtil.objectQueryString({
          w: 800,
          h: 533,
          fit: 'crop',
        }),
        'imageVariant.landscape-crop3x': sdkUtil.objectQueryString({
          w: 1200,
          h: 800,
          fit: 'crop',
        }),
        'imageVariant.landscape-crop4x': sdkUtil.objectQueryString({
          w: 1600,
          h: 1067,
          fit: 'crop',
        }),
        'imageVariant.surround-crop': sdkUtil.objectQueryString({
          w: 3040,
          h: 1520,
          fit: 'crop',
        }),
        'fields.listing': [
          'availabilityPlan',
          'createdAt',
          'description',
          'geolocation',
          'metadata.annualPassPrice',
          'metadata.explorerListing',
          'metadata.rating',
          'metadata.ratingCount',
          'price',
          'publicData.activities',
          'publicData.allWheelDrive',
          'publicData.amenities',
          'publicData.amountOfSeats',
          'publicData.bookingLimit',
          'publicData.bookingMinimum',
          'publicData.cancellationPolicy',
          'publicData.cancellationPolicyTier',
          'publicData.category',
          'publicData.closedPeriods',
          'publicData.customLineItems',
          'publicData.earliestArrivalTime',
          'publicData.expressBookingConsent',
          'publicData.extraLineItems',
          'publicData.guestOptions',
          'publicData.imagesMetadata',
          'publicData.kfz',
          'publicData.latestArrivalTime',
          'publicData.latestDepartureTime',
          'publicData.listingCategory',
          'publicData.locationPlace',
          'publicData.responseTime',
          'publicData.rules',
          'publicData.seasonalPrices',
          'publicData.surroundings',
          'publicData.translations',
          'publicData.verified',
          'state',
          'title',
        ],
      };

      try {
        const show = isOwn ? sdk.ownListings.show(params) : sdk.listings.show(params);
        const response = await show;
        const { currentUser } = getState().user;
        const providerId = response.data.included.find((i) => i.type === 'user')?.id;
        if (currentUser && hasCurrentUserValidAnnualPass(currentUser) && !isOwn) {
          const annualPass = currentUser.attributes.profile.privateData.annualPass;
          dispatch(fetchTransactionsWithProvider(providerId.uuid, annualPass));
        }
        providerId && dispatch(fetchTranslations({ user: { ids: [providerId?.uuid] } }, locale));
        dispatch(addMarketplaceEntities(response));
        return response;
      } catch (e) {
        dispatch(showListingError(storableError(e)));
        logError(e, 'show-listing-failed', { listingId });
      }
    };

export const fetchReviews = (listingId, locale) => async (dispatch, getState, sdk) => {
  dispatch(fetchReviewsRequest());
  try {
    const response = await sdk.reviews.query({
      listingId,
      state: 'public',
      include: ['author', 'author.profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    });

    const reviews = denormalisedResponseEntities(response);
    if (reviews.length > 0) {
      dispatch(fetchTranslations({ review: { ids: reviews.map((r) => r.id?.uuid) } }, locale));
    }

    dispatch(addMarketplaceEntities(response));
    dispatch(fetchReviewsSuccess(reviews));
    return reviews;
  } catch (e) {
    dispatch(fetchReviewsError(storableError(e)));
  }
};

export const fetchTimeSlots =
  (listingId, page = 0) =>
    async (dispatch, getState, sdk) => {
      try {
        if (page < 0) return;

        dispatch(fetchTimeSlotsRequest());

        let start = moment().utc().startOf('day').add(page, 'months').subtract(30, 'days').toDate();
        let end = moment().utc().startOf('day').add(page, 'months').add(60, 'days').toDate();

        if (moment(start).isBefore(moment().utc())) {
          start = moment().utc().startOf('day').toDate();
        }

        if (moment(end).isAfter(moment().add(1, 'year'))) {
          end = moment().add(1, 'year').toDate();
        }

        const response = await sdk.timeslots.query({ listingId, start, end });
        /** @type {import('@vansite/ts-sharetribe-flex-sdk').TimeSlot[]} */
        let timeSlots = denormalisedResponseEntities(response);

        const { currentUser } = getState().user;
        if (currentUser) {
          const hasAnnualPass = hasCurrentUserValidAnnualPassType(currentUser, PASS_ANNUAL_PASS_VOUCHER);
          const currentUserOpenBookings = getState().user.currentUserOpenBookings;
          if (hasAnnualPass && currentUserOpenBookings.length > 0) {
            for (const openBooking of currentUserOpenBookings) {
              timeSlots = timeSlots.map((ts) => {
                const bookingStart = moment(openBooking.attributes.start);
                const bookingEnd = moment(openBooking.attributes.end);
                const slotStart = moment(ts.attributes.start);

                if ((slotStart.isSameOrAfter(bookingStart) && slotStart.isBefore(bookingEnd))) {
                  ts.attributes.isBooked = true;
                }
                return ts;
              }).filter((ts) => ts !== null);
            }
          }
        }

        dispatch(fetchTimeSlotsSuccess(timeSlots));
        return timeSlots;
      } catch (e) {
        dispatch(fetchTimeSlotsError(storableError(e)));
        logError(e, 'fetching-time-slots-failed', { listingId: listingId.uuid });
      }
    };

/**
 * Fetch speculative transaction line items for price preview
 *
 * @param {Object} params - The parameters object
 * @param {Object} params.bookingData - Booking details
 * @param {Date|string} params.bookingData.startDate - Booking start date
 * @param {Date|string} params.bookingData.endDate - Booking end date
 * @param {Object} [params.bookingData.guestCount] - Guest count by type
 * @param {number} [params.bookingData.guestCount.adult] - Number of adults
 * @param {number} [params.bookingData.guestCount.child] - Number of children
 * @param {number} [params.bookingData.guestCount.toddler] - Number of toddlers
 * @param {number} [params.bookingData.guestCount.pet] - Number of pets
 * @param {Object} [params.bookingData.voucher] - Voucher/discount code details
 * @param {Object} [params.bookingData.customLineItems] - Custom line items selected by user
 * @param {Object} [params.bookingData.extraLineItems] - Extra line items
 * @param {import('@vansite/ts-sharetribe-flex-sdk').UUID} params.listingId - The listing UUID
 * @param {boolean} [params.isOwnListing] - Whether the current user owns the listing
 * @returns {Function} Redux thunk action
 */
// A date that is present but unparseable or has a nonsense year (e.g. "0002"
// from a half-typed year in the date input) must not reach the API — it only
// produces a 400. Absent dates are allowed (non-booking price previews).
const isGarbledBookingDate = (date) => {
  if (date == null) return false;
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return true;
  const year = parsed.getUTCFullYear();
  return year < 2000 || year > 2100;
};

export const fetchTransactionLineItems =
  ({ bookingData, listingId, isOwnListing }) =>
    async (dispatch, getState) => {
      if (isGarbledBookingDate(bookingData.startDate) || isGarbledBookingDate(bookingData.endDate)) {
        return;
      }
      dispatch(fetchLineItemsRequest());
      const { currentUser } = getState().user;
      const voucherifyCustomer = currentUser?.attributes?.profile?.privateData?.voucherifyCustomer;
      const ownedPassVoucher = buildOwnedAnnualPassVoucher(
        currentUser?.attributes?.profile?.privateData?.annualPass,
      );
      if (ownedPassVoucher) {
        bookingData.annualPassVoucher = ownedPassVoucher;
      }
      if (bookingData.voucher) dispatch(voucherValidateRequest());

      try {
        const response = await transactionLineItems({
          bookingData,
          listingId,
          isOwnListing,
          voucherifyCustomer,
        });
        const lineItems = response.data;
        const voucher = response.voucher || null;
        const annualPassVoucher = response.annualPassVoucher || null;

        dispatch(fetchLineItemsSuccess(lineItems));
        if (annualPassVoucher) dispatch(annualPassValidateSuccess({ annualPassVoucher }));
        if (bookingData.voucher) {
          if (voucher?.error || voucher?.valid === false) {
            dispatch(voucherValidateFailed(voucher.error || { message: 'Voucher not valid' }));
          } else {
            dispatch(voucherValidateSuccess({ voucher }));
          }
        }
      } catch (e) {
        dispatch(fetchLineItemsError(storableError(e)));
        // If a voucher was being validated, dispatch the voucher validation error
        if (bookingData.voucher) {
          dispatch(voucherValidateFailed(storableError(e)));
        }
        logError(e, 'fetching-line-items-failed', {
          listingId: listingId.uuid,
          bookingData: bookingData,
        });
      }
    };

export const fetchTransactionsWithProvider =
  (providerId, annualPass) => async (dispatch, getState, sdk) => {
    const { toManyTransactionsWithProviderInProgress } = getState().ListingPage;
    if (toManyTransactionsWithProviderInProgress) {
      return;
    }

    dispatch(fetchTransactionsWithProviderRequest());
    try {
      const response = await sdk.transactions.query({
        only: 'order',
        userId: providerId,
        createdAtStart: moment.utc(annualPass.dateOfPublication).toISOString(),
      });
      dispatch(addMarketplaceEntities(response));
      const { data: transactions } = response.data;
      const hasToManyTransactionsWithProvider =
        transactions
          .filter((transaction) => {
            const voucher =
              transaction?.attributes?.metadata?.annualPassVoucher ||
              transaction?.attributes?.metadata?.voucher;
            if (voucher) {
              return voucher?.code === annualPass.code;
            } else {
              return false;
            }
          })
          .filter((transaction) => {
            const { transitions } = transaction.attributes;
            const canceled = transitions.find((t) => t.transition.includes('cancel'));
            const withdrawn = transitions.find((t) => t.transition.includes('withdraw'));
            const completed = transitions.find((t) => t.transition.includes('complete'));
            const accepted = transitions.find((t) => t.transition.includes('accept'));
            const declined = transitions.find((t) => t.transition.includes('decline'));
            const transactionIsOpen =
              accepted && !canceled && !withdrawn && !completed && !declined;
            const transactionIsCompleted = completed && !canceled && !withdrawn && !declined;
            return transactionIsOpen || transactionIsCompleted;
          }).length >= ANNUAL_PASS_SAME_PROVIDER_BOOKING_LIMIT;
      dispatch(fetchTransactionsWithProviderSuccess(hasToManyTransactionsWithProvider));
    } catch (e) {
      dispatch(fetchTransactionsWithProviderError(storableError(e)));
    }
  };

export const fetchNearbyListings = (listing) => async (dispatch, getState, sdk) => {
  const geolocation = listing?.attributes?.geolocation;
  const listingId = listing?.id;
  if (!geolocation || !listingId) {
    return;
  }

  dispatch(fetchNearbyRequest());
  try {
    const response = await sdk.listings.query({
      // origin is passed directly — do NOT gate on config.sortSearchByDistance,
      // otherwise distance sorting is dropped (see SearchPage.helpers.js:136).
      origin: new LatLng(geolocation.lat, geolocation.lng),
      bounds: boundsAroundPoint(geolocation, NEARBY_RADIUS_KM),
      per_page: NEARBY_CANDIDATE_COUNT,
      ...NEARBY_LISTING_FIELDS,
    });

    // The query is centred on this listing's coordinates, so the listing itself
    // comes back in the results with the SPARSE fieldset above (no surroundings,
    // amenities, vehicles, timings, full image variants, …). If we let
    // addMarketplaceEntities merge that, the sparse publicData/images would
    // clobber the full entity loaded by showListing and the detail sections
    // would vanish. Strip the current listing — and its images — from the
    // response before normalizing.
    const currentItem = response.data.data.find((l) => l?.id?.uuid === listingId.uuid);
    const currentImageIds = new Set(
      (currentItem?.relationships?.images?.data || []).map((img) => img.id.uuid),
    );
    response.data.data = response.data.data.filter((l) => l?.id?.uuid !== listingId.uuid);
    if (Array.isArray(response.data.included)) {
      response.data.included = response.data.included.filter(
        (inc) => !(inc.type === 'image' && currentImageIds.has(inc.id.uuid)),
      );
    }

    // Stamp distance (relative to THIS listing) so ListingCard shows the badge.
    response.data.data.forEach((l) => {
      const km = l.attributes?.geolocation
        ? getDistanceBetween(geolocation, l.attributes.geolocation)
        : null;
      l.attributes.distance = km != null ? String(Math.round(km)) : undefined;
    });

    // Card fieldset — merge into, never replace, listings stored by full fetches.
    dispatch(addMarketplaceEntities(response, { sparse: true }));

    const ranked = rankNearbyListings(response.data.data, listingId.uuid);
    dispatch(fetchNearbySuccess(ranked.map((l) => l.id)));
  } catch (e) {
    dispatch(fetchNearbyError(storableError(e)));
    logError(e, 'fetch-nearby-listings-failed', { listingId: listingId?.uuid });
  }
};

export const loadData = (params, search, match, renderStatic = false) => (dispatch) => {
  const locale = getLocaleFromPath(match.path);
  const listingId = new UUID(params.id);
  const translationQuery = { listing: { ids: [listingId?.uuid] } };

  if (!validate(listingId.uuid)) {
    dispatch(showListingError(storableError({
      'name': 'AxiosError',
      'message': 'Request failed with status code 404',
      'status': 404,
      'statusText': 'ERR_BAD_REQUEST',
      'data': {
        'errors': [{
          'id': new UUID().uuid,
          'status': 404,
          'code': 'not-found',
          'title': 'Not found.',
        }],
      },
    })));
    return Promise.resolve();
  }

  const ownListingVariants = [LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT];
  if (ownListingVariants.includes(params.variant)) {
    return Promise.all([
      dispatch(showListing(listingId, locale, true)),
      dispatch(fetchTranslations(translationQuery, locale)),
    ]);
  }

  // Note: the nearby-listings query is intentionally NOT triggered here. It
  // depends on the listing's geolocation (only known after showListing) and is
  // below the fold / not SEO-critical, so awaiting it would slow SSR with an
  // extra sequential round-trip. Firing it un-awaited here doesn't work either:
  // during SSR it never lands in the serialized state, and the client router
  // does not re-run loadData after hydration — leaving the section stuck on
  // skeletons. It is fetched on the client from ListingPage instead.
  const showListingCall = dispatch(showListing(listingId, locale));

  if (renderStatic) {
    return Promise.all([
      showListingCall,
      dispatch(fetchReviews(listingId, locale)),
      dispatch(fetchTranslations(translationQuery, locale)),
    ]);
  }

  if (config.enableAvailability) {
    return Promise.all([
      showListingCall,
      dispatch(fetchReviews(listingId, locale)),
      dispatch(fetchCurrentUserHasOrders()),
      dispatch(fetchTimeSlots(listingId)),
      dispatch(fetchTranslations(translationQuery, locale)),
    ]);
  } else {
    return Promise.all([
      showListingCall,
      dispatch(fetchReviews(listingId, locale)),
      dispatch(fetchTranslations(translationQuery, locale)),
    ]);
  }
};

// Injected lazily: the reducer reaches the store when this (code-split) module is evaluated.
reducerRegistry.register('ListingPage', listingPageReducer);
